// client/src/hooks/useLeagueData.js
import { useState, useEffect, useMemo, useCallback } from 'react'
import ApiLeagues from '../api/ApiLeagues'
import ApiRegistrations from '../api/ApiRegistrations'
import ApiDivisions from '../api/ApiDivisions'
import ApiMatches from '../api/ApiMatches'
import ApiPlayoffs from '../api/ApiPlayoffs'
import toastr from 'toastr'
import { isPlayoffPhase } from '../utils/leagueStatusUtils'

const mergeLeagueWithLevels = (existingLeague, updatedLeague, leagueLevels) => {
  const mergedLeague = {
    ...(existingLeague || {}),
    ...updatedLeague,
  }

  if (Array.isArray(leagueLevels)) {
    mergedLeague.league_levels = leagueLevels
  }

  return mergedLeague
}

/**
 * Custom hook to manage league data fetching and state
 *
 * Handles loading leagues, players, divisions, assignments, matches, and playoff brackets.
 * Provides memoized selectors for optimized rendering.
 *
 * @param {string} selectedLeagueId - The currently selected league ID
 * @param {Function} setLoadingState - Function to set loading states (from useModalState)
 * @returns {Object} League data and methods to fetch/refresh
 */
export default function useLeagueData(selectedLeagueId, setLoadingState) {
  // State
  const [leagues, setLeagues] = useState([])
  const [players, setPlayers] = useState([])
  const [divisions, setDivisions] = useState([])
  const [assignments, setAssignments] = useState({})
  const [matches, setMatches] = useState([])
  const [bracket, setBracket] = useState(null)

  // Memoized selectors
  const selectedLeague = useMemo(
    () => leagues.find((l) => l.id === parseInt(selectedLeagueId)),
    [leagues, selectedLeagueId],
  )

  const playersById = useMemo(() => {
    const map = new Map()
    for (const p of players) map.set(p.id, p)
    return map
  }, [players])

  const divisionsById = useMemo(() => {
    const map = new Map()
    for (const d of divisions) map.set(d.id, d)
    return map
  }, [divisions])

  // Fetch all leagues
  const getLeagues = useCallback(async () => {
    try {
      const fetchedLeagues = await ApiLeagues.getAll()
      setLeagues(fetchedLeagues)
    } catch (err) {
      toastr.error('Failed to load leagues')
      console.error(err)
    }
  }, [])

  // Load bracket data for a specific league
  const loadBracketData = useCallback(
    async (leagueId, leagueLevelId = null) => {
      if (setLoadingState) {
        setLoadingState('loadingBracket', true)
      }
      try {
        const result = await ApiPlayoffs.getBracket(leagueId, leagueLevelId)
        setBracket(result.bracket)
      } catch (err) {
        console.error('Failed to load bracket data:', err)
        // Don't show error toast for missing bracket - it might not exist yet
      } finally {
        if (setLoadingState) {
          setLoadingState('loadingBracket', false)
        }
      }
    },
    [setLoadingState],
  )

  // Load all data for a specific league
  const loadLeagueData = useCallback(
    async (leagueId) => {
      try {
        // Use Promise.allSettled so a failure in one call (e.g. getPlayersByLeague
        // 500-ing due to a missing league_levels table pre-migration) does not
        // prevent the remaining data (divisions, assignments, matches) from loading.
        const results = await Promise.allSettled([
          ApiRegistrations.getPlayersByLeague(leagueId),
          ApiDivisions.getByLeagueId(leagueId),
          ApiRegistrations.getAssignmentsByLeague(leagueId),
          ApiMatches.getByLeague(leagueId),
        ])

        const fetchedPlayers =
          results[0].status === 'fulfilled' ? results[0].value : []
        const fetchedDivisions =
          results[1].status === 'fulfilled' ? results[1].value : []
        const assignmentList =
          results[2].status === 'fulfilled' ? results[2].value : []
        const matchesData =
          results[3].status === 'fulfilled' ? results[3].value : {}

        // Log individual failures without blocking the rest of the UI
        const labels = ['players', 'divisions', 'assignments', 'matches']
        let hadFailure = false
        results.forEach((r, i) => {
          if (r.status === 'rejected') {
            hadFailure = true
            console.error(`loadLeagueData: ${labels[i]} failed:`, r.reason)
          }
        })
        if (hadFailure) {
          toastr.warning('Some league data failed to load')
        }

        setPlayers(fetchedPlayers)
        setDivisions(fetchedDivisions)
        setMatches(matchesData.matches || [])

        // Build assignments map
        const map = {}
        for (const { division_id, player_id } of assignmentList) {
          if (!map[division_id]) map[division_id] = []
          map[division_id].push(player_id)
        }
        setAssignments(map)

        const [leagueResult, leagueLevelsResult] = await Promise.allSettled([
          ApiLeagues.getLeague(leagueId),
          ApiLeagues.getLeagueLevels(leagueId),
        ])

        if (leagueResult.status === 'fulfilled' && leagueResult.value) {
          const leagueLevels =
            leagueLevelsResult.status === 'fulfilled' &&
            Array.isArray(leagueLevelsResult.value?.league_levels)
              ? leagueLevelsResult.value.league_levels
              : undefined

          setLeagues((prev) => {
            const existingLeague = prev.find((l) => l.id === parseInt(leagueId))
            const mergedLeague = mergeLeagueWithLevels(
              existingLeague,
              leagueResult.value,
              leagueLevels,
            )

            if (existingLeague) {
              return prev.map((l) =>
                l.id === mergedLeague.id ? mergedLeague : l,
              )
            }

            return [...prev, mergedLeague]
          })
        } else if (leagueResult.status === 'rejected') {
          console.error('loadLeagueData: league failed:', leagueResult.reason)
        }

        if (leagueLevelsResult.status === 'rejected') {
          console.error(
            'loadLeagueData: league levels failed:',
            leagueLevelsResult.reason,
          )
        }

        if (
          leagueResult.status === 'fulfilled' &&
          leagueResult.value &&
          isPlayoffPhase(leagueResult.value.league_status)
        ) {
          await loadBracketData(leagueId)
        }
      } catch (err) {
        toastr.error('Failed to load league data')
        console.error(err)
      }
    },
    [loadBracketData],
  )

  // Effects
  useEffect(() => {
    getLeagues()
  }, [getLeagues])

  useEffect(() => {
    if (selectedLeagueId) loadLeagueData(selectedLeagueId)
  }, [selectedLeagueId, loadLeagueData])

  return {
    // State
    leagues,
    setLeagues,
    players,
    setPlayers,
    divisions,
    setDivisions,
    assignments,
    setAssignments,
    matches,
    setMatches,
    bracket,
    setBracket,

    // Memoized selectors
    selectedLeague,
    playersById,
    divisionsById,

    // Methods
    loadLeagues: getLeagues,
    loadLeagueData,
    loadBracketData,
    refreshLeagues: getLeagues,
  }
}
